import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ReportStatus } from '../../api';
import * as Types from '../../types';
import { ButtonGroup } from '../atoms';
import { Modal } from '../molecules';
import { Callout } from '../molecules/Callout';
import { PublicLoggedInContainerWithNavbar } from '../organismsLegacy/LoggedInContainer/PublicLoggedInContainerWithNavbar';
import { BackTextLink } from '../public/PublicReport/PublicReport.styles';
import {
  ChatColumn,
  ChatHeader,
  ChatViewContainer,
  HeaderContainer,
  ModalDescription,
  ModalDescriptionBold,
  ReportDetailColumn,
  UnpaddedColumn,
} from './ChatContainer.styles';
import ChatList from './ChatList';
import ChatReportClosedBanner from './ChatReportClosedBanner';
import ChatReportSidebar from './ChatReportSidebar';
import ChatSendMessage from './ChatSendMessage';

interface Props {
  organisation: string;
  currentUser: Types.User;
  publicReports: Types.Report[];
  messages: Map<string, Types.Message[]>;
  reportId: string;
  sendMessage: (reportId: string, text: string) => void;
  readMessages: (reportId: string) => void;
  translateMessages: boolean;
  updateTranslateMessages: (translate: boolean) => Promise<void>;
  isDynamicTranslationsEnabled: boolean;
}

const ChatContainer = ({
  organisation,
  currentUser,
  publicReports,
  messages,
  reportId,
  sendMessage,
  readMessages,
  translateMessages,
  updateTranslateMessages,
  isDynamicTranslationsEnabled,
}: Props) => {
  const { t } = useTranslation();
  const reportMessages = messages.get(reportId) ?? [];
  const report = publicReports.find((r) => r.reportId === reportId);
  const closed = report?.status === ReportStatus.CLOSED;
  const [showBanner, toggleBanner] = React.useState(true);
  const [openModal, setOpenModal] = React.useState(false);

  React.useEffect(() => {
    readMessages(reportId);
  });

  const handleToggleTranslation = () => {
    const newTranslationValue = !translateMessages;
    updateTranslateMessages(newTranslationValue);
  };

  const showMoreModal = () => {
    return (
      <Modal
        title={t('public.ChatContainer.titlev2')}
        setOpen={setOpenModal}
        open={openModal}
        onConfirm={() => setOpenModal(false)}
        size={'medium'}
        data-testid={'reshub-message-translation-learn-more-modal'}
        confirmButtonText={t('public.ChatContainer.understood')}
      >
        <ModalDescription>
          {t('public.ChatContainer.description')}
          <br />
          {t('public.ChatContainer.autoTranslate')}
          <br />
          {t('public.ChatContainer.changeInProfile')}{' '}
          <ModalDescriptionBold>
            {t('public.ChatContainer.messageSettings')}
          </ModalDescriptionBold>
          <br />
          <br />
          {t('public.ChatContainer.descriptionSecondHalf')}
        </ModalDescription>
      </Modal>
    );
  };

  return (
    <PublicLoggedInContainerWithNavbar
      organisation={organisation}
      currentUser={currentUser}
      selectedTabIndex={1}
      hideMenusWhenSmall
      shadowBackground
      hideID
    >
      <ChatViewContainer>
        <UnpaddedColumn>
          <ChatReportClosedBanner
            toggleBanner={toggleBanner}
            showBanner={closed && showBanner}
          />
          <ChatColumn>
            <ChatHeader>
              <HeaderContainer>
                <BackTextLink
                  to={`/${organisation}/open-reporting/messages`}
                  data-testid={'back-to-messages'}
                >
                  <KeyboardArrowLeft />
                  {t('public.ChatContainer.backToMessages')}
                </BackTextLink>
                {isDynamicTranslationsEnabled && (
                  <ButtonGroup
                    buttonWidth="180px"
                    options={[
                      t('public.ChatContainer.translatedMessages'),
                      t('public.ChatContainer.originalMessages'),
                    ]}
                    disabled={false}
                    setSelectedOption={() => handleToggleTranslation()}
                    selectedOption={
                      translateMessages
                        ? t('public.ChatContainer.translatedMessages')
                        : t('public.ChatContainer.originalMessages')
                    }
                    data-testid="message-translation-button"
                  />
                )}
              </HeaderContainer>
              {isDynamicTranslationsEnabled && (
                <Callout
                  onClick={() => {
                    setOpenModal(true);
                  }}
                  data-testid="opr-message-translation-learn-more-button"
                  header={t('public.ChatContainer.showMoreBanner')}
                  bodyStyle={{ width: 'auto' }}
                />
              )}
            </ChatHeader>
            <ChatList messages={reportMessages!} />
            <ChatSendMessage
              onSend={(text) => sendMessage(reportId, text)}
              placeholder={t('public.ChatContainer.messagePlaceholder')}
            />
          </ChatColumn>
        </UnpaddedColumn>
        <ReportDetailColumn data-testid={'report-detail-column'}>
          <ChatReportSidebar
            linkToViewReport={true}
            organisation={organisation}
            report={report!}
          />
        </ReportDetailColumn>
      </ChatViewContainer>
      {showMoreModal()}
    </PublicLoggedInContainerWithNavbar>
  );
};

export default ChatContainer;
